import { toNestError } from '@hookform/resolvers';
import deepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash';
import memoizeOne from 'memoize-one';
import {
  FieldError,
  FieldValues,
  Resolver,
  appendErrors,
} from 'react-hook-form';
import * as Yup from 'yup';
import { VALIDATION_VALUES } from './validationValuesKey';

/**
 * Why `path!` ? because it could be `undefined` in some case
 * https://github.com/jquense/yup#validationerrorerrors-string--arraystring-value-any-path-string
 */
const parseErrorSchema = (
  error: Yup.ValidationError,
  validateAllFieldCriteria: boolean,
) => {
  return (error.inner || []).reduce<Record<string, FieldError>>(
    (previous, error) => {
      if (!previous[error.path!]) {
        previous[error.path!] = { message: error.message, type: error.type! };
      }

      if (validateAllFieldCriteria) {
        const types = previous[error.path!].types;
        const messages = types && types[error.type!];

        previous[error.path!] = appendErrors(
          error.path!,
          validateAllFieldCriteria,
          previous,
          error.type!,
          messages
            ? ([] as string[]).concat(messages as string[], error.message)
            : error.message,
        ) as FieldError;
      }

      return previous;
    },
    {},
  );
};

/**
 * Versione memoizzata del resolver di Yup (`yupResolver` di RHF)
 */
export function yupMemoizedResolver<TFieldValues extends FieldValues>(
  schema:
    | Yup.ObjectSchema<TFieldValues>
    | ReturnType<typeof Yup.lazy<Yup.ObjectSchema<TFieldValues>>>,
  schemaOptions: Parameters<(typeof schema)['validate']>[1] = {},
  resolverOptions: {
    /**
     * Return the raw input values rather than the parsed values.
     * @default false
     */
    raw?: boolean;
  } = {},
): Resolver<Yup.InferType<typeof schema>> {
  /**
   * Memoizziamo la funzione di validazione per evitare di ricalcolare
   * lo schema di validazione ad ogni `register` (rendering di `useController`)
   */
  const memoizedValidate = memoizeOne(
    (values: any, context: any) => {
      try {
        const result = schema.validateSync(
          values,
          Object.assign({ abortEarly: false }, schemaOptions, {
            context: { ...context, [VALIDATION_VALUES]: values },
          }),
        );
        return { type: 'success', result } as const;
      } catch (e: any) {
        if (!e.inner) {
          throw e;
        }
        return { type: 'error', error: e } as const;
      }
    },
    (newArgs, lastArgs) => {
      const isSame = deepEqual(newArgs, lastArgs);
      // if (!isSame) {
      //   console.log(`[memo] recalculating`, deepDiff(lastArgs, newArgs));
      // }
      return isSame;
    },
  );

  return async (values, context, options) => {
    if (schemaOptions.context && process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        "You should not used the yup options context. Please, use the 'useForm' context object instead",
      );
    }

    // Cloniamo a prescindere: Il costo è alto, ma sicuramente più basso della
    // validazione con Yup
    const clonedValues = cloneDeep(values);
    const result = memoizedValidate(clonedValues, context);

    if (result.type === 'error') {
      return {
        values: {},
        errors: toNestError(
          parseErrorSchema(
            result.error,
            !options.shouldUseNativeValidation &&
              options.criteriaMode === 'all',
          ),
          options,
        ),
      };
    }

    return {
      values: resolverOptions.raw ? values : result.result,
      errors: {},
    };
  };
}
